import { Button } from "design-system/atoms/Button"
import { Popover, PopoverContent, PopoverTrigger } from "design-system/atoms/Popover"
import { useAuthEmployeeQuery } from "modules/auth/hooks/useAuthEmployeeQuery"
import { useConversationMessageCapabilityExecutionsQuery } from "modules/conversations/hooks/useConversationMessageCapabilityExecutionsQuery"
import { Loading } from "modules/shared/components/Loading"
import { useDeveloperMode } from "modules/shared/hooks/useDeveloperMode"
import { useTranslation } from "react-i18next"
import { BackofficeType } from "schema"
import { CapabilityExecutionDetails } from "./CapabilityExecutionDetails"
import { CapabilityExecutionSummary } from "./CapabilityExecutionSummary"

type Props = { messageId: string }

export function MessageCapabilityExecutions({ messageId }: Props) {
  const { t } = useTranslation()
  const { developerMode } = useDeveloperMode()
  const { data: employee } = useAuthEmployeeQuery()
  const includeRaw = developerMode && employee?.backofficeType === BackofficeType.Superadmin
  const { data, isPending, isError } = useConversationMessageCapabilityExecutionsQuery(
    messageId,
    includeRaw,
  )

  if (isPending) {
    return <Loading />
  }

  if (isError) {
    return (
      <span className="text-sm text-destructive">
        {t("conversations.chat.messageCapabilityExecutions.error")}
      </span>
    )
  }

  if (!data?.length) {
    return (
      <span className="text-sm text-muted-foreground">
        {t("conversations.chat.messageCapabilityExecutions.noData")}
      </span>
    )
  }

  return (
    <div className="flex flex-col gap-1">
      {data.map((execution) => (
        <Popover key={execution.id}>
          <PopoverTrigger asChild>
            <Button variant="ghost" className="h-auto w-full justify-start px-2 py-2">
              <CapabilityExecutionSummary execution={execution} />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="flex max-w-md flex-col gap-3">
            <CapabilityExecutionDetails execution={execution} />
          </PopoverContent>
        </Popover>
      ))}
    </div>
  )
}
